import { ensure } from '../types';

import { getDayDifference, getLocalDate } from './dateUtils';

export interface DateRange<T extends Date | string = Date> {
  from: T;
  to: T;
}

export interface DateRangeInstance {
  from: Date;
  to: Date;
  toDateRange: () => DateRange;
  isOverlappingWith: (other: DateRangeInstance) => boolean;
  isFullyContainedIn: (other: DateRangeInstance) => boolean;
  isConsecutiveTo: (other: DateRangeInstance, maxDayDifferenceBetweenRanges?: number) => boolean;
  isEqualTo: (other: DateRangeInstance) => boolean;
  startsBeforeAndOverlapsWith: (other: DateRangeInstance) => boolean;
  endsAfterAndOverlapsWith: (other: DateRangeInstance) => boolean;
  extendRangeEndTo: (other: DateRangeInstance) => DateRangeInstance;
  trimTo: (other: DateRangeInstance) => DateRangeInstance;
  getLaterStartDate: (other: DateRangeInstance) => Date;
  getEarlierEndDate: (other: DateRangeInstance) => Date;
}

export const dateRange = <T extends Date | string>(args: DateRange<T>): DateRangeInstance => {
  const rawFrom = getLocalDate(args.from);
  const rawTo = getLocalDate(args.to);

  ensure(rawFrom, 'Invalid date range start date');
  ensure(rawTo, 'Invalid date range end date');

  const _from = new Date(new Date(rawFrom).setHours(0, 0, 0, 0));
  const _to = new Date(new Date(rawTo).setHours(0, 0, 0, 0));

  const toDateRange = (): DateRange => ({
    from: _from,
    to: _to,
  });

  const isOverlappingWith = (other: DateRangeInstance): boolean => {
    return _from <= other.to && _to >= other.from;
  };

  const isFullyContainedIn = (other: DateRangeInstance): boolean => {
    return _from >= other.from && _to <= other.to;
  };

  const isConsecutiveTo = (other: DateRangeInstance, maxDayDifferenceBetweenRanges = 1): boolean => {
    return (
      getDayDifference(_from, other.to) <= maxDayDifferenceBetweenRanges ||
      getDayDifference(other.from, _to) <= maxDayDifferenceBetweenRanges
    );
  };

  const isEqualTo = (other: DateRangeInstance): boolean => {
    return _from.getTime() === other.from.getTime() && _to.getTime() === other.to.getTime();
  };

  const startsBeforeAndOverlapsWith = (other: DateRangeInstance): boolean => {
    return _from < other.from && _to >= other.from;
  };

  const endsAfterAndOverlapsWith = (other: DateRangeInstance): boolean => {
    return _to > other.to && _from <= other.to;
  };

  const extendRangeEndTo = (other: DateRangeInstance): DateRangeInstance => {
    return dateRange({
      from: _from,
      to: other.to > _to ? other.to : _to,
    });
  };

  const trimTo = (other: DateRangeInstance): DateRangeInstance => {
    if (!isOverlappingWith(other))
      return dateRange({
        from: _from,
        to: _to,
      });

    return dateRange({
      from: getLaterStartDate(other),
      to: getEarlierEndDate(other),
    });
  };

  const getLaterStartDate = (other: DateRangeInstance): Date => {
    return _from < other.from ? other.from : _from;
  };

  const getEarlierEndDate = (other: DateRangeInstance): Date => {
    return _to < other.to ? _to : other.to;
  };

  return {
    from: _from,
    to: _to,
    toDateRange,
    isOverlappingWith,
    isFullyContainedIn,
    isConsecutiveTo,
    isEqualTo,
    startsBeforeAndOverlapsWith,
    endsAfterAndOverlapsWith,
    extendRangeEndTo,
    trimTo,
    getLaterStartDate,
    getEarlierEndDate,
  };
};
